// src/app/proyectos/sensorica/page.tsx
'use client';

import { Box, Container, Grid, Typography, Tabs, Tab } from '@mui/material';
import { useState } from 'react';
import PhaseCard from '@/components/app/proyectos/sensorica/PhaseCard';
import ProjectTimeline from '@/components/app/proyectos/sensorica/ProjectTimeline';
import ProjectOverview from '@/components/app/proyectos/sensorica/ProjectOverview';
import { sensoricaPhases, projectOverview } from '@/data/proyectos/sensorica/sensoricaData';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TimelineIcon from '@mui/icons-material/Timeline';

export default function SensoricaPage() {
  const [viewMode, setViewMode] = useState<'cards' | 'timeline'>('cards');

  const handleViewChange = (_event: React.SyntheticEvent, newValue: 'cards' | 'timeline') => {
    setViewMode(newValue);
  };

  return (
    <Box>
      {/* Vista general del proyecto */}
      <ProjectOverview overview={projectOverview} />

      {/* Selector de vista */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          position: 'static',
          top: 64,
          zIndex: 100,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Tabs
              value={viewMode}
              onChange={handleViewChange}
              aria-label="vista de fases"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 60,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                },
              }}
            >
              <Tab
                value="cards"
                label="Vista de Tarjetas"
                icon={<ViewModuleIcon />}
                iconPosition="start"
              />
              <Tab
                value="timeline"
                label="Línea Temporal"
                icon={<TimelineIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Contenido principal */}
      {viewMode === 'cards' ? (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight={700}>
              Fases del Proyecto
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              Un enfoque sistemático de 7 fases para la monitorización integral de la calidad del agua
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {sensoricaPhases.map((phase, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={phase.id}>
                <PhaseCard phase={phase} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      ) : (
        <>
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Container maxWidth="lg">
              <Typography variant="h3" component="h2" gutterBottom fontWeight={700}>
                Cronología del Proyecto
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Seguimiento temporal de las fases de implementación
              </Typography>
            </Container>
          </Box>
          <ProjectTimeline phases={sensoricaPhases} />
        </>
      )}

      {/* Sección de tecnologías */}
      <Box
        sx={{
          py: 8,
          bgcolor: 'grey.50',
          mt: 6,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom fontWeight={700} textAlign="center">
            Stack Tecnológico
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
          >
            Combinación de tecnologías de vanguardia para un sistema robusto y escalable
          </Typography>

          <Grid container spacing={3}>
            {[
              { category: 'Hardware', items: ['Sensores multiparamétricos', 'Transmisores 4G', 'PLCs industriales', 'Sistema de limpieza automático'] },
              { category: 'Software', items: ['Next.js / React', 'FastAPI','Python / Scikit-learn', 'PostgreSQL'] },
              { category: 'Machine Learning', items: ['XGBoost', 'Random Forests', 'SVM', 'K-Nearest Neighbors', 'AdaBoost'] },
              { category: 'Infraestructura', items: ['VPS dedicado', 'Protocolos SSL/TLS', 'Sistema de backup', 'Monitoreo 24/7'] },
            ].map((tech, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 1,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
                    {tech.category}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {tech.items.map((item, idx) => (
                      <Typography
                        component="li"
                        variant="body2"
                        color="text.secondary"
                        key={idx}
                        sx={{ mb: 1 }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}